import React, { useMemo } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { DataGridPremium, GridRowEditStopReasons } from '@mui/x-data-grid-premium';

import { useCurrentUser } from 'components/root/Container/CurrentUserContainer';

import Icon from '../../../../../../../../../common/general/Icon';
import { useGetRowHeight } from '../../../../../../../../../common/hooks/useGetRowHeight';
import { muiDataGridLocaleText } from '../../../../../../../../../services/i18n/mui';

import { COMMENT_ROW_HEIGHT, EMPTY_TEXT } from './taskCommentConstants';
import createTaskCommentsColumns from './taskCommentsGridColumns';
import { CurrentUser, TaskComment, TaskCommentsModalComponent } from './taskCommentTypes';
import useTaskCommentsModal from './useTaskCommentsModal';

import './TaskCommentsModal.scss';

const TaskIcon = Icon as React.ComponentType<{ children: React.ReactNode }>;

const TaskCommentsModal: TaskCommentsModalComponent = ({
  modalBox,
  taskId,
  ssuId,
  taskComments,
  onCommentsChanged,
  initWithDraftComment = false
}) => {
  const user = useCurrentUser() as CurrentUser;
  const userFirstName = user?.firstName || '';
  const userLastName = user?.lastName || '';
  const initialRows = useMemo(() => createRows(taskComments || []), [taskComments]);
  const getRowHeight = useGetRowHeight();
  const {
    rows,
    rowModesModel,
    setRowModesModel,
    isSaving,
    currentCommentText,
    setCurrentCommentText,
    handleAddRow,
    markSaveIntent,
    handleSaveClick,
    handleCancelClick,
    handleClose,
    processRowUpdate
  } = useTaskCommentsModal({
    initialRows,
    taskId,
    ssuId,
    modalBox,
    onCommentsChanged,
    userFirstName,
    userLastName,
    initWithDraftComment
  });

  const columns = useMemo(
    () =>
      createTaskCommentsColumns({
        currentCommentText,
        rowModesModel,
        isSaving,
        onCommentChange: setCurrentCommentText,
        onAddRow: handleAddRow,
        onMarkSaveIntent: markSaveIntent,
        onSaveClick: handleSaveClick,
        onCancelClick: handleCancelClick
      }),
    [
      currentCommentText,
      rowModesModel,
      isSaving,
      setCurrentCommentText,
      handleAddRow,
      markSaveIntent,
      handleSaveClick,
      handleCancelClick
    ]
  );

  return (
    <>
      <div className="task-comments-modal-header">
        <h3>Task Comments</h3>
        <Tooltip title="Close" placement="top">
          <IconButton
            size="medium"
            onClick={handleClose}
            aria-label="Close"
            className="task-comments-modal-close-button"
          >
            <TaskIcon>close</TaskIcon>
          </IconButton>
        </Tooltip>
      </div>
      <div className="task-comments-modal-grid">
        <DataGridPremium
          editMode="row"
          rows={rows}
          columns={columns}
          rowModesModel={rowModesModel}
          onRowModesModelChange={setRowModesModel}
          processRowUpdate={processRowUpdate}
          onRowEditStop={(params, event) => {
            if (params.reason === GridRowEditStopReasons.rowFocusOut) {
              event.defaultMuiPrevented = true;
            }
          }}
          isCellEditable={({ row }) => Boolean(row.isNew)}
          rowHeight={COMMENT_ROW_HEIGHT}
          getRowHeight={getRowHeight}
          getEstimatedRowHeight={() => COMMENT_ROW_HEIGHT}
          getRowClassName={({ row }) =>
            row.isNew ? 'task-comments-modal-edit-row' : 'task-comments-modal-existing-row'
          }
          disableRowSelectionOnClick
          hideFooter
          disableColumnMenu
          disableColumnSorting
          disableColumnReorder
          disableColumnResize
          disableVirtualization
          scrollbarSize={0}
          localeText={muiDataGridLocaleText({ noRowsLabel: EMPTY_TEXT })}
          initialState={{
            sorting: {
              sortModel: [{ field: 'creationDate', sort: 'desc' }]
            }
          }}
        />
      </div>
    </>
  );
};

TaskCommentsModal.size = 'w1100';
TaskCommentsModal.className = 'task-comments-modal';

export default TaskCommentsModal;

function createRows(comments: TaskComment[]) {
  return comments.map((comment, index) => ({
    id: createRowId(comment, index),
    creationDate: comment.creationDate || null,
    createdByFirstName: comment.createdByFirstName || '',
    createdByLastName: comment.createdByLastName || '',
    commentText: comment.commentText || '',
    isNew: false
  }));
}

function createRowId(comment: TaskComment, index: number) {
  return comment.id || `${comment.creationDate || 'comment'}-${comment.createdByFirstName || ''}-${index}`;
}
